import { webApi } from '@mi-front/api-clients';
import { useEffect, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { VisibleTimeRange } from '../lib/visible-time-range';
import { useReportViewStore } from './view.store';

/**
 * Keeps the stream quality view store aligned with the current report window (`data.from` / `data.to`).
 */
export function useSyncViewStore(fromIso: string, toIso: string) {
  const initializeFromReportWindow = useReportViewStore((s) => s.initializeFromReportWindow);

  useEffect(() => {
    initializeFromReportWindow(fromIso, toIso);
  }, [fromIso, toIso, initializeFromReportWindow]);

  useEffect(() => {
    return () => {
      useReportViewStore.getState().resetViewState();
    };
  }, []);
}

/**
 * Resolves the brush/chart visible window: uses store when it matches this report's domain,
 * otherwise the full report range (covers the frame before sync runs).
 * Delegates to `getEffectiveVisibleRangeMs` on the view store.
 */
export function useEffectiveVisibleRange(
  report: webApi.StreamQualityReportResponseDto,
): VisibleTimeRange {
  const getEffectiveVisibleRangeMs = useReportViewStore((s) => s.getEffectiveVisibleRangeMs);
  const domainSync = useReportViewStore(
    useShallow((s) => ({
      vf: s.visibleRangeMs?.fromMs,
      vt: s.visibleRangeMs?.toMs,
      df: s.reportDomainMs?.fromMs,
      dt: s.reportDomainMs?.toMs,
    })),
  );

  return useMemo(() => {
    const ms = getEffectiveVisibleRangeMs(report.data.from, report.data.to);
    return { from: new Date(ms.fromMs), to: new Date(ms.toMs) };
  }, [
    report.data.from,
    report.data.to,
    getEffectiveVisibleRangeMs,
    domainSync.vf,
    domainSync.vt,
    domainSync.df,
    domainSync.dt,
  ]);
}
